import type { components } from "./api-types.gen";

export type Account = components["schemas"]["Account"];
export type User = components["schemas"]["User"];
export type Membership = components["schemas"]["Membership"];
export type Template = components["schemas"]["Template"];
export type TemplateDetail = components["schemas"]["TemplateDetail"];
export type CoverImage = components["schemas"]["CoverImage"];
export type TemplateVariation = components["schemas"]["TemplateVariation"];
export type BusinessInfo = components["schemas"]["BusinessInfo"];
export type Site = components["schemas"]["Site"];
export type TemplateCategory = components["schemas"]["TemplateCategory"];
export type TemplateSubcategory = components["schemas"]["TemplateSubcategory"];

// Online stores (e-commerce v1). Money is integer minor units (kobo, cents).
export type StoreCategory = components["schemas"]["StoreCategory"];
export type Store = components["schemas"]["Store"];
export type StoreInput = components["schemas"]["StoreInput"];
export type ProductLayout = components["schemas"]["ProductLayout"];
export type CardShape = components["schemas"]["CardShape"];
export type DeliveryOption = components["schemas"]["DeliveryOption"];
export type BankDetails = components["schemas"]["BankDetails"];
export type Product = components["schemas"]["Product"];
export type ProductInput = components["schemas"]["ProductInput"];
export type ProductImage = components["schemas"]["ProductImage"];
export type ProductOption = components["schemas"]["ProductOption"];
export type Order = components["schemas"]["Order"];
export type PublicOrder = components["schemas"]["PublicOrder"];
export type PublicStore = components["schemas"]["PublicStore"];
export type CheckoutInput = components["schemas"]["CheckoutInput"];
export type CheckoutResult = components["schemas"]["CheckoutResult"];

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "/api/v1").replace(/\/+$/, "");
const CSRF_COOKIE = "zirro_csrf";
const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  const part = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(prefix));
  return part ? decodeURIComponent(part.slice(prefix.length)) : undefined;
}

/**
 * Typed fetch wrapper for the Zirro API. Always sends cookies, attaches the
 * double-submit CSRF token on mutating requests, and throws ApiError with the
 * server's error code on non-2xx responses (PRD §11.4).
 */
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers(options.headers);
  // FormData sets its own multipart boundary header.
  if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (MUTATING.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) {
      headers.set("X-CSRF-Token", csrf);
    }
  }

  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    method,
    headers,
    credentials: "include",
  });

  const text = await res.text();
  const body: unknown = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const errorBody = (body as { error?: { code?: string; message?: string } })
      ?.error;
    throw new ApiError(
      res.status,
      errorBody?.code ?? "INTERNAL_ERROR",
      errorBody?.message ?? res.statusText,
    );
  }
  return body as T;
}

export function postJSON<T>(path: string, payload: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
